import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../firebase';

/**
 * Getting started guides are served from Storage rather than bundled into the app,
 * so guides can be added or corrected without rebuilding and redeploying the dashboard.
 * Everything below lives under this one prefix.
 */
export const GUIDES_STORAGE_PREFIX = 'Dashboard/GettingStarted';

const GUIDES_MANIFEST_PATH = 'guides.json';

/**
 * Optional local source for the guides, used to preview them during development
 * without uploading anything. Unset everywhere else, where guides come from Storage.
 */
const localGuidesBaseUrl = (process.env.REACT_APP_GUIDES_BASE_URL ?? '').replace(/\/+$/, '');

export type GuideItemType = 'article' | 'pdf';

export type GuideItem = {
    id: string;
    type: GuideItemType;
    title: string;
    summary?: string;
    // Storage path relative to GUIDES_STORAGE_PREFIX.
    thumbnail?: string;
    // Storage path relative to GUIDES_STORAGE_PREFIX, for type 'article'.
    article?: string;
    // Absolute URL of an already-hosted PDF, for type 'pdf'.
    pdfUrl?: string;
    comingSoon?: boolean;
};

export type GuideSection = {
    id: string;
    title: string;
    description?: string;
    items: Array<GuideItem>;
};

export type GuidesManifest = {
    version: number;
    hero?: { title?: string; subtitle?: string };
    sections: Array<GuideSection>;
};

/** Matches a leading YAML front matter block. */
const FRONT_MATTER = /^\uFEFF?---\r?\n[\s\S]*?\r?\n---[ \t]*\r?\n?/;

/** Match relative media paths separately from optional Markdown titles (image captions). */
const relativeMediaPattern = () => /!\[([^\]]*)\]\((?!https?:|guide:|mailto:|#)([^\s)]+)(\s+(?:"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\((?:\\.|[^)\\])*\)))?\s*\)/g;

/** Resolve a guide file's Storage path into a download URL the browser can use. */
export async function guideFileUrl(path: string): Promise<string> {
    // A local base serves the same layout straight from disk, bypassing Storage entirely.
    if (localGuidesBaseUrl) {
        return `${localGuidesBaseUrl}/${path}`;
    }
    return await getDownloadURL(ref(storage, `${GUIDES_STORAGE_PREFIX}/${path}`));
}

/**
 * Fetch a guide text file. Guides are expected to change between visits, so the
 * request is made unique to defeat any cached copy and keep a browser refresh enough
 * to show an edit.
 */
async function fetchGuideText(path: string): Promise<string> {
    const url = await guideFileUrl(path);
    const separator = url.includes('?') ? '&' : '?';
    const response = await fetch(`${url}${separator}_=${Date.now()}`);
    if (!response.ok) {
        throw new Error(`Could not load ${path} (${response.status})`);
    }
    return await response.text();
}

/** Reject a manifest that is not shaped as expected rather than rendering a broken page. */
function validateManifest(value: unknown): GuidesManifest {
    const manifest = value as GuidesManifest;
    if (!manifest || !Array.isArray(manifest.sections)) {
        throw new Error('guides.json is missing its sections');
    }
    return manifest;
}

/** Load the list of sections and guide cards. */
export async function loadGuidesManifest(): Promise<GuidesManifest> {
    return validateManifest(JSON.parse(await fetchGuideText(GUIDES_MANIFEST_PATH)));
}

/**
 * Load one article's Markdown, swapping its Storage-relative media paths for real
 * download URLs so the renderer can display them directly.
 */
export async function loadGuideArticle(path: string): Promise<string> {
    // Strip any front matter so an editor's notes never reach the page.
    const markdown = (await fetchGuideText(path)).replace(FRONT_MATTER, '');

    // Collect each distinct media path once, so a repeated image resolves a single time.
    const paths = new Set<string>();
    const scanner = relativeMediaPattern();
    let match = scanner.exec(markdown);
    while (match !== null) {
        paths.add(match[2]);
        match = scanner.exec(markdown);
    }
    if (paths.size === 0) {
        return markdown;
    }

    const resolved = await Promise.all(
        Array.from(paths).map(async (mediaPath) => {
            try {
                return [mediaPath, await guideFileUrl(mediaPath)] as const;
            } catch {
                // A missing image should not take the whole article down.
                return [mediaPath, ''] as const;
            }
        })
    );
    const urlByPath = new Map(resolved);

    return markdown.replace(relativeMediaPattern(), (whole, alt: string, mediaPath: string, title?: string) => {
        const url = urlByPath.get(mediaPath);
        // Only replace the destination; keep the original caption and its Markdown delimiters.
        return url ? `![${alt}](${url}${title ?? ''})` : whole;
    });
}

/** Resolve the thumbnail for every card, keyed by item id. Missing images are skipped. */
export async function loadGuideThumbnails(sections: Array<GuideSection>): Promise<Record<string, string>> {
    const items = sections.flatMap((section) => section.items).filter((item) => !!item.thumbnail);
    const resolved = await Promise.all(
        items.map(async (item) => {
            try {
                return [item.id, await guideFileUrl(item.thumbnail as string)] as const;
            } catch {
                return [item.id, ''] as const;
            }
        })
    );

    const urlById: Record<string, string> = {};
    resolved.forEach(([id, url]) => {
        if (url) {
            urlById[id] = url;
        }
    });
    return urlById;
}
